import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { PRODUCTS, CATEGORIES } from '../mock/mockData';
import { getCatalogCategories, getCatalogProducts } from '../services/catalogService';
import { Filter } from 'lucide-react';

const ShopPage = () => {
  const { category } = useParams();
  const [params] = useSearchParams();
  const q = (params.get('q') || '').toLowerCase();

  const [sort, setSort] = useState('popular');
  const [priceFilter, setPriceFilter] = useState('all');
  const [catalog, setCatalog] = useState(PRODUCTS);
  const [categories, setCategories] = useState(CATEGORIES);
  const [source, setSource] = useState('mock');

  useEffect(() => {
    let mounted = true;
    getCatalogProducts({ limit: 100 }).then(async ({ products, source: dataSource }) => {
      if (!mounted) return;
      setCatalog(products);
      setSource(dataSource);
      const { categories: catalogCategories } = await getCatalogCategories(products);
      if (mounted) setCategories(catalogCategories);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const list = useMemo(() => {
    const applyFilters = (products) => {
      let l = products.slice();
      if (category) l = l.filter((p) => p.category === category);
      if (q) l = l.filter((p) => p.name.toLowerCase().includes(q) || p.category?.includes(q));
      if (priceFilter === 'lt10') l = l.filter((p) => p.price < 10000);
      if (priceFilter === '10to30') l = l.filter((p) => p.price >= 10000 && p.price <= 30000);
      if (priceFilter === '30to60') l = l.filter((p) => p.price > 30000 && p.price <= 60000);
      if (priceFilter === 'gt60') l = l.filter((p) => p.price > 60000);
      if (sort === 'price-asc') l.sort((a, b) => a.price - b.price);
      if (sort === 'price-desc') l.sort((a, b) => b.price - a.price);
      if (sort === 'rating') l.sort((a, b) => b.rating - a.rating);
      if (sort === 'discount') l.sort((a, b) => b.discount - a.discount);
      return l;
    };

    const backendOrMockList = applyFilters(catalog);
    if (backendOrMockList.length || source === 'mock') return backendOrMockList;
    return applyFilters(PRODUCTS);
  }, [catalog, category, q, sort, priceFilter, source]);

  const catName = category
    ? (categories.find((c) => c.id === category)?.name || category.replace(/-/g, ' '))
    : 'All Furniture';

  return (
    <main className="bg-[#FAF7F2] min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 py-4 text-xs text-[#1F2A44]/70">
        <Link to="/" className="hover:text-[#E8B530]">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-[#E8B530]">Shop</Link>
        {category && <><span className="mx-2">/</span><span className="text-[#1F2A44] font-medium">{catName}</span></>}
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-6">
        <h1 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44]">{catName}</h1>
        <p className="text-sm text-[#1F2A44]/70 mt-1">
          {list.length} products {source === 'mock' ? '(sample catalog)' : ''}
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 pb-16 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="bg-white border border-[#1F2A44]/10 p-5 h-fit lg:sticky lg:top-32">
          <div className="flex items-center gap-2 mb-4 text-[#1F2A44]">
            <Filter size={16} />
            <h3 className="font-semibold uppercase text-sm tracking-wider">Filters</h3>
          </div>
          <div className="mb-5">
            <p className="text-xs text-[#1F2A44]/60 uppercase mb-2 font-semibold tracking-wider">Category</p>
            <ul className="space-y-1.5">
              <li><Link to="/shop" className={`block text-sm py-1 transition-colors ${!category ? 'text-[#E8B530] font-semibold' : 'text-[#1F2A44] hover:text-[#E8B530]'}`}>All</Link></li>
              {categories.map((c) => (
                <li key={c.id}><Link to={`/shop/${c.id}`} className={`block text-sm py-1 transition-colors ${category === c.id ? 'text-[#E8B530] font-semibold' : 'text-[#1F2A44] hover:text-[#E8B530]'}`}>{c.name}</Link></li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs text-[#1F2A44]/60 uppercase mb-2 font-semibold tracking-wider">Price</p>
            {[
              { id: 'all', label: 'All Prices' },
              { id: 'lt10', label: 'Under ₹10,000' },
              { id: '10to30', label: '₹10,000 - ₹30,000' },
              { id: '30to60', label: '₹30,000 - ₹60,000' },
              { id: 'gt60', label: 'Above ₹60,000' },
            ].map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-[#1F2A44] py-1 cursor-pointer hover:text-[#E8B530]">
                <input type="radio" name="price" checked={priceFilter === p.id} onChange={() => setPriceFilter(p.id)} className="accent-[#E8B530]" />
                {p.label}
              </label>
            ))}
          </div>
        </aside>

        {/* Grid */}
        <div>
          <div className="flex justify-end mb-4">
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-[#1F2A44]/20 bg-white text-[#1F2A44] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#E8B530]">
              <option value="popular">Sort: Popular</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
              <option value="discount">Biggest Discount</option>
            </select>
          </div>

          {list.length === 0 ? (
            <div className="text-center py-20 bg-white border border-[#1F2A44]/10">
              <p className="text-[#1F2A44]/70">No products found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-3 gap-4 md:gap-5">
              {list.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ShopPage;
